import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  reservedCrewNumberSchema,
  type ReservedCrewNumberInput,
} from '@/schemas/reservedCrewNumber';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, FormSelect, FormTextarea } from '../ui';
import { useProductsSelect } from '@/hooks/useProductsSelect';

interface ReservedCrewNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReservedCrewNumberInput) => Promise<void>;
}

export function ReservedCrewNumberModal({
  isOpen,
  onClose,
  onSubmit,
}: ReservedCrewNumberModalProps) {
  const { products, loading: productsLoading } = useProductsSelect();

  const form = useForm<ReservedCrewNumberInput>({
    resolver: zodResolver(reservedCrewNumberSchema),
    defaultValues: {
      product_id: '',
      crew_number_int: 1,
      reason: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ product_id: '', crew_number_int: 1, reason: '' });
    }
  }, [isOpen, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: ReservedCrewNumberInput) => {
    await onSubmit(data);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reserver un numero d'equipage"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Reserver"
          isSubmitting={form.formState.isSubmitting}
          formId="reserved-crew-number-form"
        />
      }
    >
      <Form {...form}>
        <form id="reserved-crew-number-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormSelect
              control={form.control as never}
              name="product_id"
              label="Produit"
              required
              disabled={productsLoading}
              placeholder="Selectionnez un produit..."
              options={products.map((p) => ({ value: p.id, label: p.name }))}
            />

            <FormInput
              control={form.control as never}
              name="crew_number_int"
              label="Numero a reserver"
              type="number"
              required
              valueAsNumber
              placeholder="42"
            />

            <FormTextarea
              control={form.control as never}
              name="reason"
              label="Raison"
              required
              placeholder="VPI sponsor, dossier specifique..."
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
